// Formatting helpers. Dates on screen are plain-language for shop-floor readers; numbers use tabular figures in CSS.

const DATE = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric" });
const DATE_TIME = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
// Table columns keep a two-digit day so the dates line up down the column.
const DATE_TIME_SHORT = new Intl.DateTimeFormat("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
const PLANT_DAY = new Intl.DateTimeFormat("en-US", { weekday: "long", year: "numeric", month: "short", day: "numeric" });

/** An ISO date (YYYY-MM-DD) or date-time, or an em dash when there is nothing to show. Never invents a date. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : DATE.format(date);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATE_TIME.format(date);
}

/** "Oct 20, 06:02" — for table columns where the year is the same on every row and the width is needed elsewhere. */
export function formatDateTimeShort(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATE_TIME_SHORT.format(date);
}

/** "Tuesday, Oct 20, 2026" — the plant's working day, named so a reader does not have to decode an ISO date. */
export function formatPlantDay(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return Number.isNaN(date.getTime()) ? value : PLANT_DAY.format(date);
}

/** A whole or fractional number with thousands separators, or an em dash when the figure is deliberately absent. */
export function formatNumber(value: number | null | undefined, maximumFractionDigits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-US", { maximumFractionDigits });
}

/** "3 days later" / "1 day later" — the slip table never prints a bare signed integer. */
export function formatDaysMoved(days: number): string {
  const magnitude = Math.abs(days);
  const noun = magnitude === 1 ? "day" : "days";
  if (days === 0) return "no change";
  return `${magnitude} ${noun} ${days > 0 ? "later" : "earlier"}`;
}

/** How long ago, in words. Used for freshness lines. */
export function formatAgo(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "not yet recorded";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return value;
  const minutes = Math.round((now.getTime() - then.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
